import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import TavoliniPauseCard from "@/components/TavoliniPauseCard";

function Section({ title, color, children, badge }) {
    return (
        <div className="op-card p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
                <div className="op-label text-[10px]" style={{ color }}>// {title}</div>
                {badge}
            </div>
            {children}
        </div>
    );
}

export default function AdminDbMigration() {
    const [info, setInfo] = useState(null);
    const [snaps, setSnaps] = useState([]);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(null);
    const [importReport, setImportReport] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [pickedFile, setPickedFile] = useState(null);
    const [tmBridgeResult, setTmBridgeResult] = useState(null);
    const tmFileRef = useRef(null);
    const [newSnapName, setNewSnapName] = useState("");
    const [newSnapNote, setNewSnapNote] = useState("");
    const fileRef = useRef(null);

    const reload = () => {
        setErr(null);
        Promise.all([
            api.get("/admin/db/info").then((r) => setInfo(r.data)),
            api.get("/admin/db/snapshots").then((r) => setSnaps(r.data)),
        ]).catch((e) => setErr(e.message));
    };
    useEffect(reload, []);

    // Auto-poll while a snapshot is still being created in the background.
    const hasRunning = snaps.some((s) => s.status === "running");
    useEffect(() => {
        if (!hasRunning) return;
        const t = setInterval(() => {
            api.get("/admin/db/snapshots").then((r) => setSnaps(r.data)).catch(() => {});
        }, 3000);
        return () => clearInterval(t);
    }, [hasRunning]);

    const fmt = (n) => (n ?? 0).toLocaleString();

    const handle = async (label, fn) => {
        setBusy(label);
        setErr(null);
        try {
            return await fn();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally {
            setBusy(null);
        }
    };

    const doExport = () => handle("export", async () => {
        const resp = await api.get("/admin/db/export", { responseType: "blob" });
        const blob = new Blob([resp.data], { type: "application/gzip" });
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `op-db-dump-${ts}.json.gz`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    });

    const doImport = () => handle("import", async () => {
        if (!pickedFile) throw new Error("Seleziona prima un file .json.gz");
        if (confirmText !== "WIPE-AND-RESTORE") throw new Error("Devi digitare WIPE-AND-RESTORE");
        if (!window.confirm("⚠ ATTENZIONE: questa operazione CANCELLA TUTTO il database corrente.\n\nProcedere?")) throw new Error("Operazione annullata");
        const fd = new FormData();
        fd.append("file", pickedFile);
        fd.append("confirm", "WIPE-AND-RESTORE");
        const r = await api.post("/admin/db/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImportReport(r.data);
        reload();
    });

    const doSnapshot = () => handle("snap-create", async () => {
        await api.post("/admin/db/snapshots", { name: newSnapName, note: newSnapNote });
        setNewSnapName("");
        setNewSnapNote("");
        reload();
    });
    const doRestoreSnap = (snap) => handle(`snap-restore-${snap.id}`, async () => {
        const typed = window.prompt(
            `⚠ RIPRISTINO SNAPSHOT\n\nIl database verrà CANCELLATO e riportato allo stato del:\n${new Date(snap.created_at).toLocaleString("it-IT")}\n\nTutto quello che è successo DOPO andrà perso.\nConsiglio: metti prima in pausa i tavolini automatici.\n\nPer confermare scrivi esattamente il nome dello snapshot:\n${snap.name}`
        );
        if (typed === null || typed.trim() !== (snap.name || "").trim()) throw new Error("Annullato: il nome non corrisponde.");
        const r = await api.post(`/admin/db/snapshots/${snap.id}/restore`, { confirm: "RESTORE-SNAPSHOT", snapshot_name: typed.trim() });
        window.alert(`Snapshot "${r.data.snapshot_name}" ripristinato: ${r.data.total_docs.toLocaleString()} documenti.`);
        reload();
    });

    const doDeleteSnap = (snap) => handle(`snap-del-${snap.id}`, async () => {
        if (!window.confirm(`Cancellare lo snapshot "${snap.name}"?\nNon potrà più essere ripristinato.`)) throw new Error("Annullato");
        await api.delete(`/admin/db/snapshots/${snap.id}`);
        reload();
    });

    const doResetMatches = () => handle("reset-matches", async () => {
        if (!window.confirm("⚠ Azzerare TUTTI i tabellini (gol/MVP/cartellini/GK rating)?\nLe partite tornano 'scheduled', il calendario resta.\n\nProcedere?")) throw new Error("Annullato");
        const r = await api.post("/admin/db/reset-matches", { confirm: "RESET-MATCHES" });
        window.alert(`Reset completato: ${r.data.league_matches_reset} partite di campionato + ${r.data.cup_matches_reset} partite di coppa.`);
        reload();
    });

    const doResetMarket = () => handle("reset-market", async () => {
        if (!window.confirm("⚠ Cancellare TUTTE le finestre mercato + deals + aste + bid?\nI deal accettati vengono annullati: giocatori e budget tornano al venditore.\n\nProcedere?")) throw new Error("Annullato");
        const r = await api.post("/admin/db/reset-market", { confirm: "RESET-MARKET" });
        const d = r.data;
        const reverted = d.reverted_players?.length ? `\n\nGiocatori riportati: ${d.reverted_players.join(', ')}` : '';
        const budgets = Object.entries(d.budget_restored || {}).map(([k, v]) => `${k}: ${(v / 1e6).toFixed(1)}M`).join(', ');
        window.alert(`Reset mercato completato:\n• ${d.windows_deleted} finestre\n• ${d.deals_deleted} deals\n• ${d.auctions_deleted} aste\n• ${d.bids_deleted} bid${reverted}${budgets ? '\nBudget rettificati: ' + budgets : ''}`);
        reload();
    });

    const doRecomputeTreasury = () => handle("recompute", async () => {
        const r = await api.post("/admin/db/recompute-treasury");
        const d = r.data;
        const delta = d.delta;
        const msg = delta === 0
            ? `Saldo OP già corretto: ${(d.new_balance / 1e6).toFixed(2)}M (${d.movements} movimenti).`
            : `Saldo corretto da ${(d.old_balance / 1e6).toFixed(2)}M → ${(d.new_balance / 1e6).toFixed(2)}M (Δ ${(delta / 1e6).toFixed(2)}M su ${d.movements} movimenti).`;
        window.alert(msg);
        reload();
    });

    const doDedupeCareer = () => handle("dedupe-career", async () => {
        const prev = await api.post("/admin/career-history/dedupe", { preview: true });
        const p = prev.data;
        if (!p.players_affected && !p.archives_affected) {
            window.alert("Storico carriera già pulito: nessun duplicato trovato.");
            return;
        }
        const sample = (p.samples || []).slice(0, 10).map((s) => `• ${s.name} (−${s.removed})`).join("\n");
        if (!window.confirm(
            `ANTEPRIMA deduplica storico carriera:\n` +
            `• ${p.players_affected} giocatori · ${p.rows_removed} righe duplicate da rimuovere\n` +
            (p.archives_affected ? `• ${p.archives_affected} archivi · ${p.archive_rows_removed} righe\n` : "") +
            (sample ? `\nEsempi:\n${sample}\n` : "") +
            `\nRimuove le righe IDENTICHE ripetute mantenendone una. Procedere?`
        )) throw new Error("Annullato");
        const r = await api.post("/admin/career-history/dedupe", { preview: false });
        const d = r.data;
        window.alert(`Deduplica completata: ${d.rows_removed} righe rimosse su ${d.players_affected} giocatori` +
            (d.archive_rows_removed ? ` + ${d.archive_rows_removed} righe d'archivio.` : "."));
        reload();
    });

    const doLockWages = () => handle("lock-wages", async () => {
        const prev = await api.post("/admin/wages/lock-current", { preview: true });
        const n = prev.data.locked_entries || 0;
        if (!n) { window.alert("Nessuno stipendio da bloccare: sono già tutti bloccati o rinnovati."); return; }
        if (!window.confirm(`Bloccare lo stipendio ATTUALE di ${n} giocatori (mai rinnovati)?\n\nDa qui il loro stipendio non cambierà più automaticamente con età/VG/valore: cambierà solo al rinnovo. Procedere?`)) throw new Error("Annullato");
        const r = await api.post("/admin/wages/lock-current", { preview: false });
        window.alert(`Fatto: stipendi bloccati per ${r.data.locked_entries} giocatori.`);
        reload();
    });

    const doBackfillTm = () => handle("backfill-tm", async () => {
        const r = await api.post("/admin/db/backfill-tm-images");
        window.alert(`Foto Transfermarkt riempite: ${r.data.updated_entries} giocatori aggiornati (nessun altro dato modificato).`);
        reload();
    });

    const doExportTmImages = () => handle("export-tm-img", async () => {
        const { data } = await api.get("/admin/db/export-tm-images");
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "foto-transfermarkt.json"; a.click();
        URL.revokeObjectURL(url);
        window.alert(`Esportate ${data.count} foto TM.\n\nOra apri questa stessa pagina in PRODUZIONE e usa "Importa in Produzione" con questo file.`);
    });

    const doImportTmImages = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        handle("import-tm-img", async () => {
            const parsed = JSON.parse(await file.text());
            const items = Array.isArray(parsed) ? parsed : parsed.items || [];
            const { data } = await api.post("/admin/db/import-tm-images", { items });
            setTmBridgeResult(data);
            window.alert(`Import completato: ${data.applied_roster} voci rosa e ${data.applied_players} schede aggiornate (${data.received} ricevute, ${data.skipped} saltate).`);
            reload();
        });
    };


    const doSyncRoster = () => handle("sync-roster", async () => {
        if (!window.confirm("Allineare i dati delle rose (età/VG/ruolo) ai valori correnti del PES DB?\nNon modifica i contratti, sistema solo i campi snapshot disallineati.\n\nProcedere?")) throw new Error("Annullato");
        const r = await api.post("/admin/db/sync-roster-from-pesdb");
        window.alert(`Rose allineate al PES DB: ${r.data.updated_entries} giocatori aggiornati.`);
        reload();
    });

    const doSwapOpsHope = () => handle("swap-ops-hope", async () => {
        if (!window.confirm("Scambiare TUTTE le squadre tra OPS e HOPE?\nLe squadre di OPS passano in HOPE e viceversa.\nOperazione reversibile (rilanciandola si torna indietro).\n\nProcedere?")) throw new Error("Annullato");
        const r = await api.post("/teams/swap-divisions", null, { params: { code_a: "OPS", code_b: "HOPE" } });
        window.alert(`Scambio completato: ${r.data.moved_a_to_b} squadre OPS→HOPE, ${r.data.moved_b_to_a} squadre HOPE→OPS.`);
        reload();
    });

    return (
        <div data-testid="admin-db-migration-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// SISTEMA</div>
            <h2 className="op-heading text-3xl mt-2 mb-2">Migrazione & Snapshot DB</h2>
            <p className="op-mono text-xs text-t8f95a3 mb-4 max-w-3xl leading-relaxed">
                Strumenti di manutenzione database: dump completi (migrazione preview ↔ produzione), snapshot reversibili per
                sessioni di test, reset tabellini e ricalcolo saldo tesoro.
            </p>

            {err && <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 text-xs p-3 mb-3" data-testid="db-error">{err}</div>}
            <TavoliniPauseCard />

            {/* DB Info */}
            <Section title="STATO DB CORRENTE" color="rgb(var(--tc-8f95a3))">
                {info ? (
                    <div className="op-mono text-xs">
                        <div className="mb-2">
                            <span className="text-t8f95a3">database: </span>
                            <span className="text-tffffff">{info.db_name}</span>
                            <span className="text-t8f95a3 ml-4">total: </span>
                            <span className="text-t2bb6ff" data-testid="db-total-docs">{fmt(info.total_docs)}</span>
                            <span className="text-t8f95a3"> docs</span>
                        </div>
                        <details>
                            <summary className="cursor-pointer text-t8f95a3">Dettaglio per collezione</summary>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-2">
                                {Object.entries(info.collections).map(([k, v]) => (
                                    <div key={k} className="flex justify-between border border-t22252a px-2 py-0.5">
                                        <span className="text-t8f95a3">{k}</span>
                                        <span className="text-tffffff">{v}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    </div>
                ) : (
                    <div className="op-mono text-xs text-t8f95a3 animate-pulse">// caricamento…</div>
                )}
            </Section>

            {/* Snapshots */}
            <Section title="📸 SNAPSHOT (PER TEST REVERSIBILI)" color="rgb(var(--tc-7c3aed))">
                <p className="op-mono text-[11px] text-t8f95a3 mb-3">
                    Salva lo stato del DB lato server prima di fare test (risultati partite, mercato, prestiti…). Poi ripristina
                    con un clic. Salvati nella collezione <code>db_snapshots</code> (non escono dal server).
                </p>
                <div className="grid sm:grid-cols-2 gap-2 mb-3">
                    <input
                        placeholder="Nome snapshot (opzionale)"
                        value={newSnapName}
                        onChange={(e) => setNewSnapName(e.target.value)}
                        className="op-input"
                        data-testid="snap-name"
                    />
                    <input
                        placeholder="Nota (opzionale)"
                        value={newSnapNote}
                        onChange={(e) => setNewSnapNote(e.target.value)}
                        className="op-input"
                        data-testid="snap-note"
                    />
                </div>
                <button
                    onClick={doSnapshot}
                    disabled={busy === "snap-create"}
                    className="op-btn op-btn-primary px-5 py-2 disabled:opacity-50 mb-2"
                    data-testid="snap-create-btn"
                >
                    {busy === "snap-create" ? "Avvio…" : "📸 Crea snapshot ora"}
                </button>
                <p className="op-mono text-[10px] text-t8f95a3 mb-4">
                    Lo snapshot viene creato in background (a memoria costante): la pagina si aggiorna da
                    sola quando è pronto. Puoi continuare a usare il sito nel frattempo.
                </p>

                {snaps.length === 0 ? (
                    <div className="op-mono text-[11px] text-t5c6370">// Nessuno snapshot salvato</div>
                ) : (
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wider text-t8f95a3 border-b border-t22252a">
                            <tr>
                                <th className="py-2 px-2 text-left">Nome</th>
                                <th className="py-2 px-2 text-left">Quando</th>
                                <th className="py-2 px-2 text-right">Docs / Stato</th>
                                <th className="py-2 px-2 text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {snaps.map((s) => (
                                <tr key={s.id} className="border-b border-t1a1d24 hover:bg-t0f1115">
                                    <td className="py-2 px-2">
                                        <div className="text-tffffff">{s.name}</div>
                                        {s.note && <div className="text-[10px] text-t8f95a3">{s.note}</div>}
                                    </td>
                                    <td className="py-2 px-2 op-mono text-[10px] text-t8f95a3">
                                        {s.created_at?.slice(0, 19).replace("T", " ")}
                                    </td>
                                    <td className="py-2 px-2 op-mono text-right">
                                        {s.status === "running" ? (
                                            <span className="text-te8ff00 animate-pulse" data-testid={`snap-status-${s.id}`}>in creazione…</span>
                                        ) : s.status === "error" ? (
                                            <span className="text-tff3b30" title={s.error || ""} data-testid={`snap-status-${s.id}`}>errore</span>
                                        ) : (
                                            <span className="text-t2bb6ff" data-testid={`snap-status-${s.id}`}>{fmt(s.total_docs)}</span>
                                        )}
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                        <button
                                            onClick={() => doRestoreSnap(s)}
                                            disabled={!!busy || s.status !== "done"}
                                            className="op-mono text-[10px] uppercase px-2 py-1 border border-t7c3aed text-t7c3aed hover:bg-t7c3aed/10 mr-1 disabled:opacity-30 disabled:cursor-not-allowed"
                                            data-testid={`snap-restore-${s.id}`}
                                        >
                                            ↺ Ripristina
                                        </button>
                                        <button
                                            onClick={() => doDeleteSnap(s)}
                                            disabled={!!busy || s.status === "running"}
                                            className="op-mono text-[10px] uppercase px-2 py-1 border border-t22252a text-t8f95a3 hover:border-tff3b30 hover:text-tff3b30 disabled:opacity-30"
                                            data-testid={`snap-del-${s.id}`}
                                        >
                                            ✕
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>

            {/* Tools */}
            <Section title="🛠 STRUMENTI" color="rgb(var(--tc-ff7a1a))">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Reset Tabellini</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Azzera tutti i risultati delle partite "played" (campionato + coppe). Il calendario resta.
                        </div>
                        <button
                            onClick={doResetMatches}
                            disabled={busy === "reset-matches"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-tff7a1a text-tff7a1a hover:bg-tff7a1a/10 disabled:opacity-40"
                            data-testid="reset-matches-btn"
                        >
                            {busy === "reset-matches" ? "Reset…" : "↺ Reset tabellini"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Reset Mercato</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Cancella finestre + deals + aste + bid. I deal accettati vengono annullati (giocatori e budget tornano al venditore).
                        </div>
                        <button
                            onClick={doResetMarket}
                            disabled={busy === "reset-market"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-tff3b30 text-tff3b30 hover:bg-tff3b30/10 disabled:opacity-40"
                            data-testid="reset-market-btn"
                        >
                            {busy === "reset-market" ? "Reset…" : "⚠ Reset mercato"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Ricalcola saldo Tesoro OP</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Ricostruisce <code>treasury_state.balance</code> sommando tutti i movimenti del ledger.
                        </div>
                        <button
                            onClick={doRecomputeTreasury}
                            disabled={busy === "recompute"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff/10 disabled:opacity-40"
                            data-testid="recompute-treasury-btn"
                        >
                            {busy === "recompute" ? "Calcolo…" : "↻ Ricalcola saldo OP"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Blocca stipendi correnti (una tantum)</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Congela lo stipendio ATTUALE dei giocatori che non hanno mai rinnovato. Da qui lo stipendio non cambia più automaticamente con età/VG/valore: cambia solo al rinnovo. Mostra un&apos;anteprima prima di applicare.
                        </div>
                        <button
                            onClick={doLockWages}
                            disabled={busy === "lock-wages"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-te8ff00 text-te8ff00 hover:bg-te8ff00/10 disabled:opacity-40"
                            data-testid="lock-wages-btn"
                        >
                            {busy === "lock-wages" ? "Blocco…" : "🔒 Blocca stipendi correnti"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Riempi foto Transfermarkt mancanti</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Aggiunge il pallino con la foto Transfermarkt ai giocatori che ne sono privi (es. presi dagli svincolati). Prende i dati dal PES DB e NON modifica nessun altro campo (età/VG/ruolo/stipendi invariati).
                        </div>
                        <button
                            onClick={doBackfillTm}
                            disabled={busy === "backfill-tm"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-t2bb6ff text-t2bb6ff hover:bg-t2bb6ff/10 disabled:opacity-40"
                            data-testid="backfill-tm-btn"
                        >
                            {busy === "backfill-tm" ? "Riempimento…" : "🖼 Riempi foto TM"}
                        </button>
                        <div className="mt-3 pt-3 border-t border-t22252a">
                            <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                                <strong className="text-tffffff">Ponte Preview → Produzione.</strong> In produzione lo scraping è bloccato: scarica le foto in <strong>preview</strong> (scraping consentito), poi <strong>Esporta</strong> qui e <strong>Importa in Produzione</strong>. Riempie solo dove la foto manca (non sovrascrive quelle messe a mano).
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    onClick={doExportTmImages}
                                    disabled={busy === "export-tm-img"}
                                    className="op-mono text-[10px] uppercase px-3 py-1.5 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b/10 disabled:opacity-40"
                                    data-testid="export-tm-img-btn"
                                >
                                    {busy === "export-tm-img" ? "Esporto…" : "⬇ Esporta foto TM (JSON)"}
                                </button>
                                <button
                                    onClick={() => tmFileRef.current?.click()}
                                    disabled={busy === "import-tm-img"}
                                    className="op-mono text-[10px] uppercase px-3 py-1.5 border border-tffb020 text-tffb020 hover:bg-tffb020/10 disabled:opacity-40"
                                    data-testid="import-tm-img-btn"
                                >
                                    {busy === "import-tm-img" ? "Importo…" : "⬆ Importa in Produzione"}
                                </button>
                                <input
                                    ref={tmFileRef}
                                    type="file"
                                    accept="application/json,.json"
                                    className="hidden"
                                    onChange={doImportTmImages}
                                    data-testid="import-tm-img-input"
                                />
                                {tmBridgeResult && (
                                    <span className="op-mono text-[10px] text-t7ce38b w-full mt-1" data-testid="import-tm-img-result">
                                        ✓ {tmBridgeResult.applied_roster} voci rosa · {tmBridgeResult.applied_players} schede · {tmBridgeResult.received} ricevute · {tmBridgeResult.skipped} saltate
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Deduplica storico carriera</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Rimuove dallo storico carriera dei giocatori le righe IDENTICHE ripetute (stessa stagione + squadra + tipo + valore), mantenendone una sola. Mostra un&apos;anteprima prima di applicare.
                        </div>
                        <button
                            onClick={doDedupeCareer}
                            disabled={busy === "dedupe-career"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-t7ce38b text-t7ce38b hover:bg-t7ce38b/10 disabled:opacity-40"
                            data-testid="dedupe-career-btn"
                        >
                            {busy === "dedupe-career" ? "Pulizia…" : "🧹 Deduplica carriere"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Allinea Rose al PES DB</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Aggiorna età / VG / ruolo dei giocatori in rosa ai valori correnti del PES DB. Risolve gli stipendi "arancioni" disallineati.
                        </div>
                        <button
                            onClick={doSyncRoster}
                            disabled={busy === "sync-roster"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-te8ff00 text-te8ff00 hover:bg-te8ff00/10 disabled:opacity-40"
                            data-testid="sync-roster-btn"
                        >
                            {busy === "sync-roster" ? "Sync…" : "↻ Allinea rose"}
                        </button>
                    </div>
                    <div className="border border-t22252a p-3">
                        <div className="op-mono text-xs text-tffffff mb-1">Scambia OPS ↔ HOPE</div>
                        <div className="op-mono text-[10px] text-t8f95a3 mb-2">
                            Scambia tutte le squadre tra le divisioni OPS e HOPE. Reversibile (rilanciando si torna indietro).
                        </div>
                        <button
                            onClick={doSwapOpsHope}
                            disabled={busy === "swap-ops-hope"}
                            className="op-mono text-[10px] uppercase px-3 py-1.5 border border-tff7a1a text-tff7a1a hover:bg-tff7a1a/10 disabled:opacity-40"
                            data-testid="swap-ops-hope-btn"
                        >
                            {busy === "swap-ops-hope" ? "Scambio…" : "⇄ Scambia OPS/HOPE"}
                        </button>
                    </div>
                </div>
            </Section>

            {/* Export */}
            <Section title="1. EXPORT DUMP" color="rgb(var(--tc-2bb6ff))">
                <p className="op-mono text-[11px] text-t8f95a3 mb-3">
                    Genera un file <code>.json.gz</code> con TUTTE le collezioni. Usalo come backup esterno (scarica e archivia
                    su Google Drive / disco).
                </p>
                <button
                    onClick={doExport}
                    disabled={busy === "export"}
                    className="op-btn op-btn-primary px-5 py-2 disabled:opacity-50"
                    data-testid="db-export-btn"
                >
                    {busy === "export" ? "Generazione…" : "↓ Scarica dump completo"}
                </button>
            </Section>

            {/* Import */}
            <Section title="2. IMPORT DUMP (DISTRUTTIVO)" color="rgb(var(--tc-ff3b30))">
                <p className="op-mono text-[11px] text-tff3b30 mb-3">
                    ⚠ Cancella TUTTO il DB corrente e lo ricrea da un dump esterno. Usalo SOLO per la migrazione
                    preview → produzione, oppure per ripristinare un backup salvato esternamente.
                </p>

                <div className="mb-3">
                    <label className="op-mono text-[10px] uppercase tracking-wider text-t8f95a3 block mb-1">File dump (.json.gz)</label>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".gz,.json,application/gzip,application/x-gzip"
                        onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                        className="op-input w-full sm:w-96"
                        data-testid="db-import-file"
                    />
                    {pickedFile && (
                        <div className="op-mono text-[10px] text-t8f95a3 mt-1">
                            {pickedFile.name} · {(pickedFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label className="op-mono text-[10px] uppercase tracking-wider text-t8f95a3 block mb-1">
                        Conferma: digita <span className="text-tff3b30">WIPE-AND-RESTORE</span>
                    </label>
                    <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="WIPE-AND-RESTORE"
                        className="op-input w-full sm:w-96"
                        data-testid="db-import-confirm"
                    />
                </div>

                <button
                    onClick={doImport}
                    disabled={busy === "import" || !pickedFile || confirmText !== "WIPE-AND-RESTORE"}
                    className="op-mono text-xs uppercase tracking-wider px-5 py-2 border border-tff3b30 text-tff3b30 hover:bg-tff3b30/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="db-import-btn"
                >
                    {busy === "import" ? "Import in corso…" : "⚠ Cancella tutto e ripristina"}
                </button>

                {importReport && (
                    <div className="mt-4 border border-t2bb6ff/40 bg-t2bb6ff/5 p-3" data-testid="db-import-report">
                        <div className="op-label text-[10px] mb-2" style={{ color: "rgb(var(--tc-2bb6ff))" }}>// ✓ IMPORT COMPLETATO</div>
                        <div className="op-mono text-xs space-y-1">
                            <div><span className="text-t8f95a3">source DB:</span> <span className="text-tffffff">{importReport.source_db}</span></div>
                            <div><span className="text-t8f95a3">exported at:</span> <span className="text-tffffff">{importReport.source_exported_at}</span></div>
                            <div><span className="text-t8f95a3">total restored:</span> <span className="text-t2bb6ff">{fmt(importReport.total_docs)}</span> docs</div>
                        </div>
                    </div>
                )}
            </Section>
        </div>
    );
}
